//
// "This week's list" (spec: guided planning, Phase 2). ONE definition, read by
// the Week page, the Choose tasks panel and the week session, so no two
// surfaces disagree about what is on a week. Records-aware (committedTo): a
// row picked for today (dated + focused) or given a day is STILL on the list —
// the list stays whole all week (Scott, 2026-09-21). A line once marked a
// goal is a line like any other (2026-10-04: no goal/task split): given this
// week, it is on the week's list.

import { weekendLabel } from './weekend'
import type { Task } from '@/types/task'
import { committedTo, isFocused, openCommitment, sameDay } from '@/lib/placement/model'
import { doableBy } from './poolViews'
import { localYmd } from '@/lib/cadence/config'

export function weekListTasks(tasks: readonly Task[], weekStart: Date, meId: string | null, opts: { isCurrent?: boolean } = {}): Task[] {
  const out: Task[] = []
  for (const t of tasks) {
    if (meId && !doableBy(t, meId)) continue
    const c = committedTo(t, 'week', weekStart, { isCurrent: opts.isCurrent ?? true })
    if (!c) continue
    if (c !== 'legacy' && (c.status === 'carried' || c.status === 'removed')) continue
    // A legacy week row with no week of its own reads as the CURRENT week's
    // while it is open (it is this week's until placed). Once completed it
    // is history, not this week's record: Scott's chooser opened on
    // seventeen struck-through rows from January–June (2026-09-22).
    if (c === 'legacy' && t.completed && !t.weekStart) continue
    out.push(t)
  }
  return out.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
}

/** `dayLabel` is a real date ("Sat, Oct 3"), never a bare weekday: "kept from
 *  last week · Sat" read as either Saturday (Scott, 2026-09-28). `dayPassed`:
 *  the day came and went with the row still open. */
export interface WeekRowNote { origin?: 'month' | 'kept'; monthLabel?: string; dayLabel?: string; dayPassed?: boolean; pickedToday: boolean; weekend?: string }

export function weekRowNote(t: Task, weekStart: Date, userId: string | null | undefined, todayYmd: string): WeekRowNote {
  const prev = new Date(weekStart); prev.setDate(prev.getDate() - 7)
  const keptFromLast = (t.commitments ?? []).some((c) => c.level === 'week' && c.status === 'carried' && sameDay(c.periodStart, prev) && !!c.carriedTo && sameDay(c.carriedTo, weekStart))
  const month = openCommitment(t, 'month')?.periodStart ?? (t.bucket === 'month' ? t.monthStart : undefined)
  const monthLabel = month ? month.toLocaleDateString('en-US', { month: 'long' }) : undefined
  const end = new Date(weekStart); end.setDate(end.getDate() + 7)
  const inWeek = !!t.scheduledFor && t.scheduledFor >= weekStart && t.scheduledFor < end
  const dayLabel = inWeek ? t.scheduledFor!.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : undefined
  const dayPassed = inWeek && !t.completed && localYmd(t.scheduledFor!) < todayYmd
  return { weekend: t.weekendStart ? weekendLabel(t.weekendStart) : undefined, origin: keptFromLast ? 'kept' : monthLabel ? 'month' : undefined, monthLabel, dayLabel, ...(dayPassed ? { dayPassed } : {}), pickedToday: isFocused(t, userId, todayYmd) }
}

export function weekRowNoteText(n: WeekRowNote): string | undefined {
  const parts: string[] = []
  if (n.weekend) parts.push(n.weekend)
  if (n.origin === 'kept') parts.push('kept from last week')
  else if (n.origin === 'month' && n.monthLabel) parts.push(`from ${n.monthLabel}`)
  if (n.dayLabel) parts.push(n.dayPassed ? `missed ${n.dayLabel}` : n.dayLabel)
  if (n.pickedToday) parts.push('picked for today')
  return parts.length ? parts.join(' · ') : undefined
}
